// Keyboard shortcuts for ending a live call: 0 ends it, 1 files it as a voicemail.
// Matched on `key` so any layout's zero counts, and on `code` for the numpad, where
// NumLock leaves `key` as a navigation name instead of a digit.

import type { CockpitPhase } from './state';

export type HangupAction = 'end' | 'voicemail';

export interface HangupShortcutInput {
  key: string;
  code: string;
  repeat: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  editable: boolean;
  // Something is open over the card, so a keypress meant for it must not reach through
  // and end a call the SDR cannot currently see.
  modalOpen: boolean;
  phase: CockpitPhase;
  online: boolean;
}

// Typing a digit must never end a call, so anything that accepts text is off limits.
// Structural rather than `instanceof HTMLElement`: the shape is all that is read.
export function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as { tagName?: unknown; isContentEditable?: unknown } | null;
  if (!el) return false;
  if (el.isContentEditable === true) return true;
  const tag = typeof el.tagName === 'string' ? el.tagName.toUpperCase() : '';
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

// `online` is checked here and not left to the caller: the buttons carry the offline
// case as a disabled state, which a keypress has no equivalent of. Ending a call over a
// closed socket clears the card while the server is never told, so the SDR walks away
// from a call that is still live to the person on it.
//
// Shift is deliberately not blocked: layouts that need it to type a digit still report
// `key === '0'`, and where Shift changes the character the match simply fails.
export function resolveHangupShortcut(input: HangupShortcutInput): HangupAction | null {
  if (!input.online || input.phase !== 'on_call') return null;
  if (input.repeat || input.editable || input.modalOpen) return null;
  if (input.ctrlKey || input.metaKey || input.altKey) return null;

  if (input.key === '0' || input.code === 'Numpad0') return 'end';
  if (input.key === '1' || input.code === 'Numpad1') return 'voicemail';
  return null;
}
